import React, { useState } from 'react';
import axios from 'axios';
import { Link, useOutletContext } from 'react-router-dom';
import { useConfirm } from '../../ConfirmDialog';
import { contractorAuth } from '../../../lib/contractorAuth';
import { fmtDateTime } from '../../../lib/when';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * NOTES (migration 094): a box to add one on top, then the notes newest
 * first - who, when, which ticket it came from - each yours to change or
 * take back (an administrator may take back anybody's). Staff may keep a
 * note from contractors with "Staff only"; a contractor is never offered it.
 *
 * The notes themselves are the page's (AssetLayout loads them once, for
 * the count on the tab and the preview on the Overview); this tab reads
 * them from the context and asks for a reload after every change. Adding
 * sends with Ctrl/Cmd+Enter, as a ticket's reply box does.
 */
export default function NotesTab() {
  const { asset, base, notes, reloadNotes, portal, write, role, me } = useOutletContext();
  const [confirm, confirmDialog] = useConfirm();
  const [text, setText] = useState('');
  const [staffOnly, setStaffOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);   // { id, body }
  const auth = contractorAuth();
  const mayAdd = write || portal;
  const admin = role === 'admin';
  const myKey = notes?.me || (portal ? null : me?.id ? `u:${me.id}` : null);

  const add = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    setError('');
    try {
      await axios.post(`${API_BASE}/assets/${asset.id}/notes`,
        { body, ...(staffOnly && !portal ? { staff_only: true } : {}) }, auth);
      setText('');
      setStaffOnly(false);
      await reloadNotes();
    } catch (err) {
      setError(err.response?.data?.error || 'That did not go through.');
    } finally {
      setBusy(false);
    }
  };
  const save = async (e) => {
    e.preventDefault();
    const body = editing.body.trim();
    if (!body || busy) return;
    setBusy(true);
    setError('');
    try {
      await axios.patch(`${API_BASE}/assets/${asset.id}/notes/${editing.id}`, { body }, auth);
      setEditing(null);
      await reloadNotes();
    } catch (err) {
      setError(err.response?.data?.error || 'That did not go through.');
    } finally {
      setBusy(false);
    }
  };
  const remove = async (n) => {
    const mine = n.author_key === myKey;
    if (!(await confirm({
      title: mine ? 'Take this note back?' : `Take back ${n.author_name}’s note?`,
      message: 'It goes from the Notes tab. The line it wrote in the History stays.',
      confirmLabel: 'Take it back', danger: true
    }))) return;
    setError('');
    try {
      await axios.delete(`${API_BASE}/assets/${asset.id}/notes/${n.id}`, auth);
      await reloadNotes();
    } catch (err) {
      setError(err.response?.data?.error || 'That did not go through.');
    }
  };
  const onKey = (fn) => (e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) fn(e); };

  if (notes?.status === 503) {
    return (
      <div className="as-tab as-tab-notes">
        <section className="as-panel">
          <h2>Notes</h2>
          <p className="as-note" role="status">{notes.error || 'Notes on devices are not set up on this server yet - run migrate:094.'}</p>
          <p className="as-quiet">Until then, a note is a line in the <Link to={`${base}/history`}>History</Link>.</p>
        </section>
        {confirmDialog}
      </div>
    );
  }
  const list = notes?.notes || [];
  return (
    <div className="as-tab as-tab-notes">
      {mayAdd && (
        <form className="as-note-box" onSubmit={add} data-testid="add-note">
          <label className="as-field-label" htmlFor="as-note-text">Add a note</label>
          <textarea id="as-note-text" className="as-input" rows={3} value={text} maxLength={4000}
            onChange={(e) => setText(e.target.value)} onKeyDown={onKey(add)}
            placeholder={portal ? 'What you did, or what IT should know about this device.' : 'Anything worth remembering about this device.'} />
          {error && !editing && <p className="as-error" role="alert">{error}</p>}
          <div className="as-note-bar">
            {!portal && (
              <label className="as-check as-staff-only">
                <input type="checkbox" checked={staffOnly} onChange={(e) => setStaffOnly(e.target.checked)} />
                Staff only
              </label>
            )}
            <span className="as-hint as-quiet">
              {staffOnly && !portal ? 'A contractor looking at this device does not see it' : 'Goes in the History too'}
              {' · '}<kbd className="as-kbd">Ctrl</kbd>+<kbd className="as-kbd">Enter</kbd>
            </span>
            <button type="submit" className="btn btn-primary" disabled={busy || !text.trim()}>
              {busy ? 'Adding…' : 'Add note'}
            </button>
          </div>
        </form>
      )}
      {!notes && <p className="as-quiet">Loading…</p>}
      {notes && list.length === 0 && (
        <section className="as-panel">
          <p className="as-empty">
            {mayAdd ? 'No notes yet. The first one goes above.' : 'No notes on this device.'}
          </p>
        </section>
      )}
      {list.length > 0 && (
        <ul className="as-notes">
          {list.map((n) => {
            const mine = n.author_key === myKey;
            return (
              <li key={n.id} className="as-note-row" data-note={n.id} data-staff-only={n.staff_only ? 'true' : undefined}>
                <div className="as-note-head">
                  <span className="as-note-who">{n.author_name}{n.author_key?.startsWith('c:') ? <span className="as-quiet"> (contractor)</span> : null}</span>
                  <span className="as-note-when as-quiet">{fmtDateTime(n.created_at)}{n.edited_at ? ' · edited' : ''}</span>
                  {n.staff_only && <span className="as-chip" data-tone="warning">Staff only</span>}
                  {n.ticket_id && (
                    <Link className="as-chip as-chip-link" data-tone="info"
                      to={portal ? `/my-tickets/${n.ticket_id}` : `/tickets/${n.ticket_id}`}>
                      {`From ticket ${n.ticket_number || ''}`.trim()}
                    </Link>
                  )}
                  <span className="as-spacer" />
                  {mine && editing?.id !== n.id && (
                    <button type="button" className="btn btn-plain btn-small" onClick={() => { setEditing({ id: n.id, body: n.body }); setError(''); }}>Edit</button>
                  )}
                  {(mine || admin) && (
                    <button type="button" className="btn btn-plain btn-small" onClick={() => remove(n)}>Delete</button>
                  )}
                </div>
                {editing?.id === n.id ? (
                  <form className="as-note-edit" onSubmit={save}>
                    <textarea className="as-input" rows={3} value={editing.body} maxLength={4000} autoFocus
                      onChange={(e) => setEditing((x) => ({ ...x, body: e.target.value }))} onKeyDown={onKey(save)} />
                    {error && <p className="as-error" role="alert">{error}</p>}
                    <div className="as-note-bar">
                      <button type="button" className="btn btn-plain btn-small" onClick={() => setEditing(null)}>Cancel</button>
                      <button type="submit" className="btn btn-primary btn-small" disabled={busy || !editing.body.trim()}>Save</button>
                    </div>
                  </form>
                ) : (
                  <p className="as-note-body">{n.body}</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {notes?.earlier && (
        <p className="as-quiet as-earlier">Earlier notes are in the <Link to={`${base}/history`}>History</Link>.</p>
      )}
      {confirmDialog}
    </div>
  );
}
